// Business rules for agency custom booking charges. Single source of truth shared by the
// agent-side yup form schema (field-level validation/messages) and the API-side validator
// (Agency before save). Message text and error placement stay with each consumer; the rules
// themselves live here so the two layers cannot drift.

export const BOOKING_CHARGE_TYPES = ['flat', 'perPerson', 'percentage'];

export const PERCENTAGE_MAX = 100;

export const isValidChargeType = chargeType => BOOKING_CHARGE_TYPES.includes(chargeType);

// A percentage charge's amount is a rate, so it cannot exceed 100; other types are uncapped.
export const exceedsPercentageLimit = (chargeType, amount) => chargeType === 'percentage' && Number(amount) > PERCENTAGE_MAX;

// A flat charge is already a fixed amount, so a cap is meaningless and not allowed.
export const maxCapAllowedForType = chargeType => chargeType !== 'flat';

// Only a perPerson cap must clear the per-person amount. A percentage cap is an absolute ceiling
// unrelated to the rate, so it may be any positive value.
export const maxCapBelowAmount = (chargeType, amount, maxCap) => chargeType === 'perPerson' && Number(maxCap) < Number(amount);

export const BOOKING_CHARGE_ERROR = {
  NAME_REQUIRED: 'NAME_REQUIRED',
  CHARGE_TYPE_INVALID: 'CHARGE_TYPE_INVALID',
  AMOUNT_INVALID: 'AMOUNT_INVALID',
  PERCENTAGE_OVER_100: 'PERCENTAGE_OVER_100',
  CURRENCY_INVALID: 'CURRENCY_INVALID',
  MAXCAP_NOT_ALLOWED_FLAT: 'MAXCAP_NOT_ALLOWED_FLAT',
  MAXCAP_NOT_NUMBER: 'MAXCAP_NOT_NUMBER',
  MAXCAP_NOT_POSITIVE: 'MAXCAP_NOT_POSITIVE',
  MAXCAP_BELOW_AMOUNT: 'MAXCAP_BELOW_AMOUNT',
};

// Full structural + business validation for a single charge. Returns an array of
// { field, code } entries in evaluation order ([] when valid). Consumers that lack another
// validation layer (the API) call this directly and map codes to their own messages.
export const validateBookingCharge = charge => {
  const errors = [];
  const E = BOOKING_CHARGE_ERROR;

  if (typeof charge.name !== 'string' || charge.name.trim() === '') {
    errors.push({ field: 'name', code: E.NAME_REQUIRED });
  }
  if (!isValidChargeType(charge.chargeType)) {
    errors.push({ field: 'chargeType', code: E.CHARGE_TYPE_INVALID });
  }
  if (typeof charge.amount !== 'number' || charge.amount <= 0) {
    errors.push({ field: 'amount', code: E.AMOUNT_INVALID });
  } else if (exceedsPercentageLimit(charge.chargeType, charge.amount)) {
    errors.push({ field: 'amount', code: E.PERCENTAGE_OVER_100 });
  }
  if (charge.currency !== undefined && typeof charge.currency !== 'string') {
    errors.push({ field: 'currency', code: E.CURRENCY_INVALID });
  }
  if (charge.maxCap !== undefined && charge.maxCap !== null) {
    if (!maxCapAllowedForType(charge.chargeType)) {
      errors.push({ field: 'maxCap', code: E.MAXCAP_NOT_ALLOWED_FLAT });
    } else if (typeof charge.maxCap !== 'number') {
      errors.push({ field: 'maxCap', code: E.MAXCAP_NOT_NUMBER });
    } else if (charge.maxCap <= 0) {
      errors.push({ field: 'maxCap', code: E.MAXCAP_NOT_POSITIVE });
    } else if (maxCapBelowAmount(charge.chargeType, charge.amount, charge.maxCap)) {
      errors.push({ field: 'maxCap', code: E.MAXCAP_BELOW_AMOUNT });
    }
  }

  return errors;
};
